import { UserRights } from '@work4all/models/lib/additionalEnums/UserRights';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import {
  PermissionRequiredRight,
  PermissionsRequiredRightsByEntity,
} from '../types';

// ==========================================
// Common Permission Configurations
// ==========================================

const COMMON_CUSTOMER_SUPPLIER_MAR_LIST: PermissionRequiredRight[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_CRM_LIGHT,
  },
];

const COMMON_PROJECT_MAR_LIST: PermissionRequiredRight[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_PROJECT_LIGHT,
  },
];

const COMMON_TRAVEL_RECEIPTS_MAR: PermissionRequiredRight = {
  type: 'ModuleAccessRight',
  value: ModuleAccessRightType.FEATURE_RECEIPTS_TRAVEL,
};

const COMMON_TIME_TRACKING_MAR_LIST: PermissionRequiredRight[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_TICKETS_LIGHT,
    mustNotHave: true,
  },
];

// ==========================================
// General Permission Configurations
// ==========================================

export const GENERAL_PERMISSIONS_REQUIRED_RIGHTS: PermissionsRequiredRightsByEntity =
  {
    // ----------------
    // Customer
    // ----------------
    [Entities.customer]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.KundenNeuAendern },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
      canEdit: [
        { type: 'UserRightOperation', value: UserRights.KundenNeuAendern },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.KundenLoeschen },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
    },

    // ----------------
    // Supplier
    // ----------------
    [Entities.supplier]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.LieferantenNeuAendern },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
      canEdit: [
        { type: 'UserRightOperation', value: UserRights.LieferantenNeuAendern },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.LieferantenLoeschen },
        COMMON_CUSTOMER_SUPPLIER_MAR_LIST,
      ],
    },

    // ----------------
    // Project
    // ----------------
    [Entities.project]: {
      canAdd: [
        [
          { type: 'UserRight', value: UserRights.ProjekteNeu },
          {
            type: 'UserRight',
            value: UserRights.ProjekteAendernLoeschenEigene,
          },
        ],
        COMMON_PROJECT_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.ProjektAendern,
          ownlyOwnPermissionCode: UserRights.ProjekteAendernLoeschenEigene,
          ownageProperty: ['creatorUserId', 'creator.id'],
        },
        COMMON_PROJECT_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.ProjekteLoeschen },
        COMMON_PROJECT_MAR_LIST,
      ],
    },

    // ----------------
    // Article
    // ----------------
    [Entities.article]: {
      canAdd: [{ type: 'UserRight', value: UserRights.ArtikelNeuAendern }],
      canEdit: [
        { type: 'UserRightOperation', value: UserRights.ArtikelNeuAendern },
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.ArtikelLoeschen },
      ],
    },

    // ----------------
    // Email Signature
    // ----------------
    [Entities.eMailSignature]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.EMailSignaturenBearbeiten },
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.EMailSignaturenBearbeiten,
        },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.EMailSignaturenBearbeiten },
      ],
    },

    // ----------------
    // Email Template
    // ----------------
    [Entities.eMailTemplate]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.EMailSignaturenBearbeiten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.EMailSignaturenBearbeiten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.EMailSignaturenBearbeiten },
      ],
    },

    // ----------------
    // Production Contract
    // ----------------
    [Entities.productionContract]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.ProduktionsauftraegeErstellenAendern,
        },
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.ProduktionsauftraegeErstellenAendern,
        },
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.ProduktionsauftraegeLoeschen,
        },
      ],
    },

    // ----------------
    // Text Building Block
    // ----------------
    [Entities.textBuildingBlock]: {
      canAdd: [{ type: 'UserRight', value: UserRights.TextbausteineVerwalten }],
      canEdit: [
        { type: 'UserRight', value: UserRights.TextbausteineVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.TextbausteineVerwalten },
      ],
    },

    // ----------------
    // Word Document Template
    // ----------------
    [Entities.wordDocumentTemplate]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.VorlagenAnlegenLoeschen },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.VorlagenAnlegenLoeschen },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.VorlagenAnlegenLoeschen },
      ],
    },

    // ----------------
    // Word Letter Template
    // ----------------
    [Entities.wordLetterTemplate]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.VorlagenAnlegenLoeschen },
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.VorlagenAnlegenLoeschen,
        },
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.VorlagenAnlegenLoeschen,
        },
      ],
    },

    // ----------------
    // Travel Expenses
    // ----------------
    [Entities.travelExpenses]: {
      canAdd: [{ type: 'UserRight', value: UserRights.Reisekostenabrechnung }],
      canEdit: [
        { type: 'UserRightOperation', value: UserRights.Reisekostenabrechnung },
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.Reisekostenabrechnung },
      ],
    },

    // ----------------
    // Travel Receipts
    // ----------------
    [Entities.travelReceipts]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.Reisekostenabrechnung },
        COMMON_TRAVEL_RECEIPTS_MAR,
      ],
      canEdit: [
        { type: 'UserRightOperation', value: UserRights.Reisekostenabrechnung },
        COMMON_TRAVEL_RECEIPTS_MAR,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.Reisekostenabrechnung },
        COMMON_TRAVEL_RECEIPTS_MAR,
      ],
    },

    // ----------------
    // Contact
    // ----------------
    [Entities.contact]: {
      // ----------------
      // Customer Contact
      // ----------------
      customer: {
        canAdd: [
          {
            type: 'UserRight',
            value: UserRights.KundenAnsprechpartnerNeuAendern,
          },
        ],
        canEdit: [
          {
            type: 'UserRightOperation',
            value: UserRights.KundenAnsprechpartnerNeuAendern,
          },
        ],
        canDelete: [
          {
            type: 'UserRightOperation',
            value: UserRights.KundenAnsprechpartnerLoeschen,
          },
        ],
      },
      // ----------------
      // Supplier Contact
      // ----------------
      supplier: {
        canAdd: [
          {
            type: 'UserRight',
            value: UserRights.LieferantenAnsprechpartnerNeuAendern,
          },
        ],
        canEdit: [
          {
            type: 'UserRightOperation',
            value: UserRights.LieferantenAnsprechpartnerNeuAendern,
          },
        ],
        canDelete: [
          {
            type: 'UserRightOperation',
            value: UserRights.LieferantenAnsprechpartnerLoeschen,
          },
        ],
      },
    },

    // ----------------
    // Full User
    // ----------------
    [Entities.fullUser]: {
      canAdd: [{ type: 'UserRight', value: UserRights.MitarbeiterNeuAendern }],
      canEdit: [{ type: 'UserRight', value: UserRights.MitarbeiterNeuAendern }],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.MitarbeiterLoeschen },
      ],
    },

    // ----------------
    // Time Tracking
    // ----------------
    [Entities.timeTracking]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.KostenerfassungNutzen },
        ...COMMON_TIME_TRACKING_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.ZeiterfassungFuerAndereBenutzer,
          ownlyOwnPermissionCode: UserRights.KostenerfassungNutzen,
          ownageProperty: ['creator.id'],
        },
        ...COMMON_TIME_TRACKING_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.ZeiterfassungFuerAndereBenutzer,
          ownlyOwnPermissionCode: UserRights.KostenerfassungNutzen,
          ownageProperty: ['creator.id'],
        },
        ...COMMON_TIME_TRACKING_MAR_LIST,
      ],
    },

    // ----------------
    // Address Connection
    // ----------------
    [Entities.addressConnection]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.AdressverknuepfungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.AdressverknuepfungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.AdressverknuepfungVerwalten },
      ],
    },

    // ----------------
    // Ledger Account
    // ----------------
    [Entities.ledgerAccount]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },

    // ----------------
    // Tenant Bank Account
    // ----------------
    [Entities.tenantBankAccount]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },

    // Cost Center
    // ----------------
    [Entities.costCenter]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },

    // Country
    // ----------------
    [Entities.country]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },

    // ----------------
    // Numbering Logic
    // ----------------
    [Entities.numberingLogic]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },

    // ----------------
    // Tenant
    // ----------------
    [Entities.tenant]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
      canDelete: [
        { type: 'UserRight', value: UserRights.GenerelleEinstellungVerwalten },
      ],
    },
  };
